import { FormEvent, useEffect, useState } from "react";

import { useLanguage } from "@/i18n/LanguageContext";
import {
  adminCreatePastEvent,
  adminDeletePastEvent,
  adminListPastEvents,
  adminUpdatePastEvent,
  adminUploadPastEventPdf,
  type ConferencePastEvent,
} from "../api";
import { PageHeader } from "../shells/PageHeader";

const emptyForm = {
  slug: "",
  title_en: "",
  title_ar: "",
  title_fr: "",
  year: "",
  event_date: "",
  description_en: "",
  description_ar: "",
  description_fr: "",
  is_published: true,
  display_order: "0",
};

export function AdminPastEventsPage() {
  const { t, locale } = useLanguage();
  const [rows, setRows] = useState<ConferencePastEvent[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const reload = () => adminListPastEvents().then(setRows);

  useEffect(() => {
    reload().catch(() => setRows([]));
  }, []);

  const titleOf = (row: ConferencePastEvent) => {
    if (locale === "ar") return row.title_ar;
    if (locale === "fr") return row.title_fr;
    return row.title_en;
  };

  const resetForm = () => {
    setForm(emptyForm);
    setPdfFile(null);
    setEditingId(null);
  };

  const startEdit = (row: ConferencePastEvent) => {
    setEditingId(row.id);
    setForm({
      slug: row.slug,
      title_en: row.title_en,
      title_ar: row.title_ar,
      title_fr: row.title_fr,
      year: row.year != null ? String(row.year) : "",
      event_date: row.event_date ? row.event_date.slice(0, 10) : "",
      description_en: row.description_en || "",
      description_ar: row.description_ar || "",
      description_fr: row.description_fr || "",
      is_published: row.is_published,
      display_order: String(row.display_order ?? 0),
    });
    setPdfFile(null);
    setMessage(null);
    setError(null);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      if (editingId) {
        await adminUpdatePastEvent(editingId, {
          slug: form.slug.trim(),
          title_en: form.title_en.trim(),
          title_ar: form.title_ar.trim(),
          title_fr: form.title_fr.trim(),
          year: form.year.trim() ? Number(form.year) : null,
          event_date: form.event_date.trim() || null,
          description_en: form.description_en.trim() || null,
          description_ar: form.description_ar.trim() || null,
          description_fr: form.description_fr.trim() || null,
          is_published: form.is_published,
          display_order: Number(form.display_order || 0),
        });
        if (pdfFile) {
          await adminUploadPastEventPdf(editingId, pdfFile);
        }
        setMessage(t.admin.pastEventsSaved);
      } else {
        const fd = new FormData();
        fd.append("slug", form.slug.trim());
        fd.append("title_en", form.title_en.trim());
        fd.append("title_ar", form.title_ar.trim());
        fd.append("title_fr", form.title_fr.trim());
        if (form.year.trim()) fd.append("year", form.year.trim());
        if (form.event_date.trim()) fd.append("event_date", form.event_date.trim());
        if (form.description_en.trim()) fd.append("description_en", form.description_en.trim());
        if (form.description_ar.trim()) fd.append("description_ar", form.description_ar.trim());
        if (form.description_fr.trim()) fd.append("description_fr", form.description_fr.trim());
        fd.append("is_published", String(form.is_published));
        fd.append("display_order", form.display_order || "0");
        if (pdfFile) fd.append("pdf", pdfFile);
        await adminCreatePastEvent(fd);
        setMessage(t.admin.pastEventsCreated);
      }
      resetForm();
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title={t.admin.pastEvents} lead={t.admin.pastEventsLead} backTo="/admin" />
      {error && <div className="dn-alert dn-alert-error">{error}</div>}
      {message && <div className="dn-alert dn-alert-success">{message}</div>}

      <form className="dn-card" onSubmit={onSubmit} style={{ marginBottom: "1.5rem" }}>
        <h3>{editingId ? t.admin.pastEventsEdit : t.admin.pastEventsAdd}</h3>
        <div className="dn-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))" }}>
          <label>
            {t.admin.pastEventsSlug}
            <input
              className="dn-input"
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              required
            />
          </label>
          <label>
            {t.admin.pastEventsYear}
            <input
              className="dn-input"
              type="number"
              value={form.year}
              onChange={(e) => setForm((f) => ({ ...f, year: e.target.value }))}
            />
          </label>
          <label>
            {t.admin.pastEventsDate}
            <input
              className="dn-input"
              type="date"
              value={form.event_date}
              onChange={(e) => setForm((f) => ({ ...f, event_date: e.target.value }))}
            />
          </label>
          <label>
            {t.admin.pastEventsOrder}
            <input
              className="dn-input"
              type="number"
              value={form.display_order}
              onChange={(e) => setForm((f) => ({ ...f, display_order: e.target.value }))}
            />
          </label>
        </div>
        <label>
          {t.admin.pastEventsTitleEn}
          <input
            className="dn-input"
            value={form.title_en}
            onChange={(e) => setForm((f) => ({ ...f, title_en: e.target.value }))}
            required
          />
        </label>
        <label>
          {t.admin.pastEventsTitleAr}
          <input
            className="dn-input"
            value={form.title_ar}
            onChange={(e) => setForm((f) => ({ ...f, title_ar: e.target.value }))}
            required
            dir="rtl"
          />
        </label>
        <label>
          {t.admin.pastEventsTitleFr}
          <input
            className="dn-input"
            value={form.title_fr}
            onChange={(e) => setForm((f) => ({ ...f, title_fr: e.target.value }))}
            required
          />
        </label>
        <label>
          {t.admin.pastEventsDescEn}
          <textarea
            className="dn-input"
            value={form.description_en}
            onChange={(e) => setForm((f) => ({ ...f, description_en: e.target.value }))}
            rows={2}
          />
        </label>
        <label>
          {t.admin.pastEventsDescAr}
          <textarea
            className="dn-input"
            value={form.description_ar}
            onChange={(e) => setForm((f) => ({ ...f, description_ar: e.target.value }))}
            rows={2}
            dir="rtl"
          />
        </label>
        <label>
          {t.admin.pastEventsDescFr}
          <textarea
            className="dn-input"
            value={form.description_fr}
            onChange={(e) => setForm((f) => ({ ...f, description_fr: e.target.value }))}
            rows={2}
          />
        </label>
        <label>
          {t.admin.pastEventsPdf}
          <input
            className="dn-input"
            type="file"
            accept="application/pdf,.pdf"
            onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
          />
        </label>
        <label className="dn-check">
          <input
            type="checkbox"
            checked={form.is_published}
            onChange={(e) => setForm((f) => ({ ...f, is_published: e.target.checked }))}
          />{" "}
          {t.admin.pastEventsPublished}
        </label>
        <div className="dn-cta-row">
          <button className="dn-btn dn-btn-primary" type="submit" disabled={busy}>
            {busy ? t.common.loading : editingId ? t.common.save : t.admin.pastEventsAdd}
          </button>
          {editingId && (
            <button className="dn-btn dn-btn-ghost" type="button" onClick={resetForm}>
              {t.common.cancel}
            </button>
          )}
        </div>
      </form>

      <div className="dn-grid">
        {rows.map((row) => (
          <div className="dn-card" key={row.id}>
            <h3>{titleOf(row)}</h3>
            <p className="dn-muted">
              {row.slug}
              {row.year != null ? ` · ${row.year}` : ""}
              {" · "}
              {row.is_published ? t.admin.pastEventsPublished : t.admin.pastEventsArchived}
              {row.has_pdf ? ` · PDF` : ""}
            </p>
            <div className="dn-cta-row">
              <button type="button" className="dn-btn dn-btn-primary" onClick={() => startEdit(row)}>
                {t.common.edit}
              </button>
              <button
                type="button"
                className="dn-btn dn-btn-ghost"
                onClick={async () => {
                  await adminUpdatePastEvent(row.id, { is_published: !row.is_published });
                  await reload();
                }}
              >
                {row.is_published ? t.admin.pastEventsUnpublish : t.admin.pastEventsPublish}
              </button>
              <button
                type="button"
                className="dn-btn dn-btn-ghost"
                onClick={async () => {
                  if (!window.confirm(t.admin.pastEventsDeleteConfirm)) return;
                  await adminDeletePastEvent(row.id);
                  await reload();
                }}
              >
                {t.common.delete}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
